/**
 * Master Bot - Create Bot Page
 * Form to create new vendor bot
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Button from '../../components/Button';
import { useTelegram } from '../../hooks/useTelegram';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const Label = styled.label`
  font-size: var(--font-md);
  font-weight: var(--font-medium);
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--font-md);

  &:focus {
    outline: none;
    border-color: var(--accent-green);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const Textarea = styled.textarea`
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--font-md);
  min-height: 100px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--accent-green);
  }

  &::placeholder {
    color: var(--text-secondary);
  }
`;

const HelpText = styled.p`
  font-size: var(--font-sm);
  color: var(--text-secondary);
  margin-top: var(--spacing-xs);
`;

const InfoCard = styled(Card)`
  background: rgba(76, 175, 80, 0.1);
  border: 1px solid var(--accent-green);
`;

const InfoTitle = styled.h3`
  font-size: var(--font-md);
  font-weight: var(--font-semibold);
  color: var(--accent-green);
  margin-bottom: var(--spacing-sm);
`;

const InfoList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const InfoItem = styled.li`
  font-size: var(--font-sm);
  color: var(--text-secondary);
  padding: var(--spacing-xs) 0;
  padding-left: var(--spacing-md);
  position: relative;

  &:before {
    content: '✓';
    position: absolute;
    left: 0;
    color: var(--accent-green);
  }
`;

const CreateBot: React.FC = () => {
  const navigate = useNavigate();
  const { showMainButton, showBackButton, notificationFeedback } = useTelegram();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    botToken: '',
    botName: '',
    botDescription: '',
  });

  useEffect(() => {
    showBackButton(() => navigate('/master/dashboard'));
  }, [showBackButton, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Simulate API call
    setTimeout(() => {
      notificationFeedback('success');
      setLoading(false);
      navigate('/master/bots');
    }, 2000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const isFormValid = formData.botToken.trim() !== '' && formData.botName.trim() !== '';

  useEffect(() => {
    if (isFormValid) {
      showMainButton('Create Bot', () => {
        const form = document.getElementById('create-bot-form') as HTMLFormElement;
        form?.requestSubmit();
      });
    }
  }, [isFormValid, showMainButton]);

  return (
    <Layout header={<Navbar title="Create New Bot" showBack />}>
      <InfoCard>
        <InfoTitle>🎉 Free 7-Day Trial</InfoTitle>
        <InfoList>
          <InfoItem>First bot gets 7 days free trial</InfoItem>
          <InfoItem>Full feature access included</InfoItem>
          <InfoItem>No payment method required</InfoItem>
          <InfoItem>Cancel anytime before trial ends</InfoItem>
        </InfoList>
      </InfoCard>

      <Form id="create-bot-form" onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="botToken">Bot Token *</Label>
          <Input
            id="botToken"
            name="botToken"
            type="text"
            value={formData.botToken}
            onChange={handleChange}
            placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz"
            required
          />
          <HelpText>Get your bot token from @BotFather on Telegram</HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="botName">Bot Name *</Label>
          <Input
            id="botName"
            name="botName"
            type="text"
            value={formData.botName}
            onChange={handleChange}
            placeholder="My Store Bot"
            required
          />
          <HelpText>Choose a friendly name for your bot</HelpText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="botDescription">Bot Description (Optional)</Label>
          <Textarea
            id="botDescription"
            name="botDescription"
            value={formData.botDescription}
            onChange={handleChange}
            placeholder="Describe what your bot does..."
          />
          <HelpText>This will be shown to your customers</HelpText>
        </FormGroup>

        <Button type="submit" loading={loading} disabled={!isFormValid} fullWidth>
          Create Bot
        </Button>
      </Form>
    </Layout>
  );
};

export default CreateBot;
